/**
 * PromptDialog.tsx - Input prompt dialog component
 * 
 * CHANGE HISTORY:
 * - 251025_1600 [pending] danielbeer: Initial creation
 * 
 * Dialog for collecting text input from user
 */

import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { PromptConfig } from '../types';
import './PromptDialog.css';

export interface PromptDialogProps extends PromptConfig {
  isOpen: boolean;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

/**
 * PromptDialog - Text input dialog component
 * 
 * Features:
 * - Text/number/email/password input types
 * - Validation support
 * - Auto-focus on input
 * - Enter to submit
 */
export const PromptDialog: React.FC<PromptDialogProps> = ({
  isOpen,
  title,
  message,
  defaultValue = '',
  placeholder = '',
  type = 'text',
  validation,
  onSubmit,
  onCancel
}) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setValue(defaultValue);
      setError('');
      // Focus input after modal animation
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, defaultValue]);

  const handleSubmit = () => {
    // Validate if validation function provided
    if (validation) {
      const result = validation(value);
      if (result !== true) {
        setError(typeof result === 'string' ? result : 'Invalid input');
        return;
      }
    }

    onSubmit(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const footer = (
    <>
      <button className="btn btn-secondary" onClick={onCancel}>
        Cancel
      </button>
      <button className="btn btn-primary" onClick={handleSubmit}>
        OK
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      size="small"
      footer={footer}
    >
      <div className="promptr-prompt-content">
        {message && (
          <p className="promptr-prompt-message">{message}</p>
        )}
        
        <input
          ref={inputRef}
          type={type}
          className="promptr-prompt-input"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(''); // Clear error on change
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
        />

        {error && (
          <p className="promptr-prompt-error">{error}</p>
        )}
      </div>
    </Modal>
  );
};
